/**
 * AdminSettings — consolidates admin configuration screens into one tabbed page:
 * Sections, Subjects, Calls, Imports.
 */
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LayoutGrid, BookOpen, PhoneOutgoing, UploadCloud, Library, Settings as SettingsIcon, PartyPopper, CalendarHeart, ClipboardList, MonitorSmartphone, Activity, MessageCircleQuestion, FileText, Building2, CreditCard } from "lucide-react";
import AdminSections from "@/pages/AdminSections";
import AdminSubjects from "@/pages/AdminSubjects";
import VoiceCalls from "@/pages/VoiceCalls";
import AdminImports from "@/pages/AdminImports";
import AdminTuitionLibrary from "@/pages/AdminTuitionLibrary";
import AdminHolidays from "@/pages/AdminHolidays";
import AdminEvents from "@/pages/AdminEvents";
import AdminAssessmentSettings from "@/pages/AdminAssessmentSettings";
import AdminSessions from "@/pages/AdminSessions";
import AdminAudit from "@/pages/AdminAudit";
import TutorReview from "@/pages/TutorReview";
import TeacherUtility from "@/pages/TeacherUtility";
import SchoolProfile from "@/pages/SchoolProfile";
import WccBalanceCard from "@/components/WccBalanceCard";

const VALID_TABS = ["sections", "subjects", "assessments", "reports", "school-profile", "calls", "imports", "tuition-library", "holidays", "events", "sessions", "audit", "tutor-review", "wcc"];

export default function AdminSettings() {
  const { user } = useAuth();
  const isReadOnlySuper = user?.role === "super_admin";
  const [params, setParams] = useSearchParams();

  const requested = params.get("tab");
  const active = VALID_TABS.includes(requested) ? requested : "sections";

  const onChange = (val) => setParams({ tab: val }, { replace: true });

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-settings-page">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300 flex items-center justify-center">
            <SettingsIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Settings</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Manage sections, subjects, calls and data imports.</p>
          </div>
        </div>

        <Tabs value={active} onValueChange={onChange} className="w-full">
          <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="settings-tabs">
            <TabsTrigger value="sections" data-testid="settings-tab-sections" className="gap-1.5">
              <LayoutGrid className="h-4 w-4" /> Sections
            </TabsTrigger>
            <TabsTrigger value="subjects" data-testid="settings-tab-subjects" className="gap-1.5">
              <BookOpen className="h-4 w-4" /> Subjects
            </TabsTrigger>
            <TabsTrigger value="assessments" data-testid="settings-tab-assessments" className="gap-1.5">
              <ClipboardList className="h-4 w-4" /> Assessments
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="settings-tab-reports" className="gap-1.5">
              <FileText className="h-4 w-4" /> Reports
            </TabsTrigger>
            <TabsTrigger value="school-profile" data-testid="settings-tab-school-profile" className="gap-1.5">
              <Building2 className="h-4 w-4" /> School Profile
            </TabsTrigger>
            <TabsTrigger value="calls" data-testid="settings-tab-calls" className="gap-1.5">
              <PhoneOutgoing className="h-4 w-4" /> Calls
            </TabsTrigger>
            {!isReadOnlySuper && (
              <TabsTrigger value="imports" data-testid="settings-tab-imports" className="gap-1.5">
                <UploadCloud className="h-4 w-4" /> Imports
              </TabsTrigger>
            )}
            <TabsTrigger value="tuition-library" data-testid="settings-tab-tuition-library" className="gap-1.5">
              <Library className="h-4 w-4" /> Tuition Library
            </TabsTrigger>
            <TabsTrigger value="holidays" data-testid="settings-tab-holidays" className="gap-1.5">
              <PartyPopper className="h-4 w-4" /> Holidays
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="settings-tab-events" className="gap-1.5">
              <CalendarHeart className="h-4 w-4" /> Events
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="settings-tab-sessions" className="gap-1.5">
              <MonitorSmartphone className="h-4 w-4" /> Sessions &amp; Devices
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="settings-tab-audit" className="gap-1.5">
              <Activity className="h-4 w-4" /> Activity Audit
            </TabsTrigger>
            <TabsTrigger value="tutor-review" data-testid="settings-tab-tutor-review" className="gap-1.5">
              <MessageCircleQuestion className="h-4 w-4" /> AI Tutor Review
            </TabsTrigger>
            <TabsTrigger value="wcc" data-testid="settings-tab-wcc" className="gap-1.5">
              <CreditCard className="h-4 w-4" /> WCC Credits
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sections" className="mt-6"><AdminSections embedded /></TabsContent>
          <TabsContent value="subjects" className="mt-6"><AdminSubjects embedded /></TabsContent>
          <TabsContent value="assessments" className="mt-6">
            <AdminAssessmentSettings />
          </TabsContent>
          <TabsContent value="reports" className="mt-6"><TeacherUtility embedded /></TabsContent>
          <TabsContent value="school-profile" className="mt-6"><SchoolProfile /></TabsContent>
          <TabsContent value="calls" className="mt-6"><VoiceCalls embedded /></TabsContent>
          {!isReadOnlySuper && (
            <TabsContent value="imports" className="mt-6"><AdminImports /></TabsContent>
          )}
          <TabsContent value="tuition-library" className="mt-6"><AdminTuitionLibrary embedded /></TabsContent>
          <TabsContent value="holidays" className="mt-6"><AdminHolidays embedded /></TabsContent>
          <TabsContent value="events" className="mt-6"><AdminEvents embedded /></TabsContent>
          <TabsContent value="sessions" className="mt-6"><AdminSessions /></TabsContent>
          <TabsContent value="audit" className="mt-6"><AdminAudit embedded /></TabsContent>
          <TabsContent value="tutor-review" className="mt-6"><TutorReview embedded /></TabsContent>
          <TabsContent value="wcc" className="mt-6"><WccBalanceCard canSync={!isReadOnlySuper} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
